import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/index';

export type EquipmentTab = 'equipment' | 'calibration';

/**
 * Equipment list state, like the legacy `equipment` reducer: the organization
 * picked in the organization dropdown (0 = All organizations), the table search
 * within it, the Equipment table's and the Calibration Due Dates list's pages and
 * the bottom-navigation tab, kept across view/edit pages.
 */
export interface EquipmentState {
  organizationID: number;
  /** Trimmed table search; '' = none. */
  search: string;
  /** 1-based page of the Equipment table. */
  page: number;
  /** 1-based page of the Calibration Due Dates list. */
  calibrationPage: number;
  /** Rows a page, in both lists. */
  pageLimit: number;
  selectedTab: EquipmentTab;
}

export const initialEquipmentState: EquipmentState = {
  organizationID: 0,
  search: '',
  page: 1,
  calibrationPage: 1,
  pageLimit: 10,
  selectedTab: 'equipment',
};

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState: initialEquipmentState,
  reducers: {
    SET_ORGANIZATION_ID: (state, action: PayloadAction<number>) => {
      state.organizationID = action.payload;
      state.page = 1;
      state.calibrationPage = 1;
    },
    SET_SEARCH: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 1;
    },
    SET_PAGE: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    SET_CALIBRATION_PAGE: (state, action: PayloadAction<number>) => {
      state.calibrationPage = action.payload;
    },
    SET_SELECTED_TAB: (state, action: PayloadAction<EquipmentTab>) => {
      state.selectedTab = action.payload;
    },
  },
});

export const { SET_ORGANIZATION_ID, SET_SEARCH, SET_PAGE, SET_CALIBRATION_PAGE, SET_SELECTED_TAB } =
  equipmentSlice.actions;

export const equipmentStateSelector = (state: RootState) =>
  (state.equipment as EquipmentState | undefined) ?? initialEquipmentState;

export default equipmentSlice.reducer;
